// src/components/ClinicProtecMgmt/ClinicProtecMgmt.jsx (예시 경로)
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/axios.secure';
import Menu from '../Menu/Menu';
import { useAuth } from '../../auth/AuthContext';

// ───────────── 입력 규칙 공통 상수 ─────────────
const MAX_ID_LEN = 28;
const MAX_PW_LEN = 32;

// ID: 영문/숫자 + . - _
const ID_CHAR_REGEX = /^[A-Za-z0-9._-]+$/;   // 전체 문자열 검사용
const ID_CHAR_ONE = /^[A-Za-z0-9._-]$/;    // 한 글자 검사용

// 검색창: 한 글자 검사 (한글, 영문/숫자, ._-@, 공백)
const SEARCH_CHAR_ONE = /^[A-Za-z0-9._\-@가-힣\s]$/;

// 비밀번호: 영문/숫자 + !@#$%^*()_+-=
const PW_CHAR_ONE = /^[A-Za-z0-9!@#$%^*()_+\-=]$/;      // 한 글자 검사용
const PW_ALLOWED_ALL = /^[A-Za-z0-9!@#$%^*()_+\-=]+$/;     // 전체 문자열 검사용

// 값 필터링 (붙여넣기/드래그 입력 대비)
const filterIdValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => ID_CHAR_ONE.test(ch))
    .join('')
    .slice(0, MAX_ID_LEN);

const filterPwValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => PW_CHAR_ONE.test(ch))
    .join('')
    .slice(0, MAX_PW_LEN);

const filterSearchValue = (value) =>
  (value || '')
    .split('')
    .filter((ch) => SEARCH_CHAR_ONE.test(ch))
    .join('')
    .slice(0, 50);

// ───────────── 공통 유틸 ─────────────
const strongPwOk = (pw, id) => {
  if (!pw || typeof pw !== 'string') return false;
  if (pw.length < 10 || pw.length > 32) return false;
  if (/\s/.test(pw)) return false;

  // 허용 특수문자만 사용
  if (!PW_ALLOWED_ALL.test(pw)) return false;

  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSpec = /[^A-Za-z0-9]/.test(pw);
  if ([hasLower, hasUpper, hasDigit, hasSpec].filter(Boolean).length < 3) return false;

  if (id && pw.toLowerCase().includes(String(id).toLowerCase())) return false;
  return true;
};

const Badge = ({ s }) => {
  const v = String(s || '').toUpperCase();
  const cls = v === 'ACTIVE' ? 'active' : 'locked';
  return <span className={`status-pill ${cls}`}>{v || '-'}</span>;
};

// 서버 응답 파서/체크 (다른 페이지와 동일 패턴)
const parsePayload = (data) => {
  if (!data) return {};
  const payload =
    Array.isArray(data?.data)
      ? data
      : (data?.data && typeof data.data === 'object')
        ? data.data
        : data;

  const rows =
    Array.isArray(payload?.data) ? payload.data :
      Array.isArray(payload?.list) ? payload.list :
        Array.isArray(payload) ? payload : [];

  const pageV = Number(payload?.page ?? data?.page ?? 1);
  const sizeV = Number(payload?.size ?? data?.size ?? 10);
  const totalV = Number(payload?.total ?? payload?.count ?? data?.total ?? 0);
  return { rows, pageV, sizeV, totalV };
};

const isOk = (res) => {
  if (!res) return false;
  if (res?.status === true || res?.ok === true) return true;
  if (res?.messageCode === 200 && (res?.data?.status === true || res?.data?.ok === true)) return true;
  if (res?.data?.data?.status === true || res?.data?.data?.ok === true) return true;
  return false;
};

const getMsg = (res, fallback) =>
  res?.messageString || res?.message || res?.data?.message || res?.data?.messageString || fallback;

// ───────────── 메인 컴포넌트 ─────────────
export default function ClinicProtecMgmt() {
  // 공통/알림
  const { user } = useAuth();
  const role = String(user?.role || '').toUpperCase();
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2200); };

  // 목록 상태
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / (size || 10))), [total, size]);

  // 행 별 비밀번호 입력
  const [pwMap, setPwMap] = useState({}); // { [protec_id]: 'new...' }

  // 생성 폼
  const [createForm, setCreateForm] = useState({ clinic_id: '', protec_id: '', protec_password: '', status: 'ACTIVE' });

  // 빠른 매칭 상태
  const [assigning, setAssigning] = useState(null); // row.user_id

  // ───────── 입력 차단용 핸들러 ─────────
  const handleBeforeInputId = (e) => {
    if (e.isComposing) return;
    const ch = e.data;
    if (!ch) return;
    if (!ID_CHAR_ONE.test(ch)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= MAX_ID_LEN) {
      e.preventDefault();
    }
  };

  const handleBeforeInputPw = (e) => {
    if (e.isComposing) return;
    const ch = e.data;
    if (!ch) return;
    if (!PW_CHAR_ONE.test(ch)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= MAX_PW_LEN) {
      e.preventDefault();
    }
  };

  const handleBeforeInputSearch = (e) => {
    const ch = e.data;
    if (!ch) return;
    if (!SEARCH_CHAR_ONE.test(ch)) {
      e.preventDefault();
    }
  };

  // 목록 조회
  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, size };
      const t = (q || '').trim();
      if (t.length >= 2) params.q = t;

      const { data } = await api.get('/manager/user/clinic/protectors', { params });
      const { rows, pageV, sizeV, totalV } = parsePayload(data);

      setList(Array.isArray(rows) ? rows : []);
      setPage(pageV || 1);
      setSize(sizeV || 10);
      setTotal(totalV || 0);
      if (t && t.length < 2) toast('검색은 2자 이상 입력 시 적용됩니다.');
    } catch (e) {
      setList([]); setTotal(0);
      toast('목록 조회 실패');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchList(1); /* eslint-disable-next-line */ }, []);

  // 상태 변경
  const changeStatus = async (protec_id, next) => {
    setLoading(true);
    try {
      const { data } = await api.patch(
        `/manager/user/clinic/protectors/${encodeURIComponent(protec_id)}/status`,
        { status: next }
      );
      if (isOk(data)) {
        toast('상태 변경 완료');
        setList(prev => prev.map(r => r.user_id === protec_id ? { ...r, status: next } : r));
      } else {
        toast(getMsg(data, '상태 변경 실패'));
      }
    } catch (e) {
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else toast('상태 변경 실패');
    } finally { setLoading(false); }
  };

  // 비밀번호 변경
  const changePassword = async (protec_id) => {
    const pw = pwMap[protec_id] || '';
    if (!strongPwOk(pw, protec_id)) {
      return toast('비밀번호는 10~32자, 공백X, (대/소/숫/특) 3종 이상, ID 포함 금지, 허용 특수문자만 사용');
    }
    setLoading(true);
    try {
      const { data } = await api.post(
        `/manager/user/clinic/protectors/${encodeURIComponent(protec_id)}/password`,
        { new_password: pw }
      );
      if (isOk(data)) {
        toast('비밀번호 변경 완료');
        setPwMap(m => ({ ...m, [protec_id]: '' }));
        fetchList(page);
      } else {
        toast(getMsg(data, '비밀번호 변경 실패'));
      }
    } catch (e) {
      if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else if (e?.response?.status === 406) toast('비밀번호 정책 위반');
      else toast('비밀번호 변경 실패');
    } finally { setLoading(false); }
  };

  // 생성
  const createProtec = async () => {
    const { clinic_id, protec_id, protec_password, status } = createForm;
    if (!protec_id || !protec_password) return toast('아이디/비밀번호 입력');
    if (role === 'ADMIN' && !clinic_id) return toast('ADMIN은 clinic_id를 입력해야 합니다');

    // ID 형식 검증
    if (!ID_CHAR_REGEX.test(protec_id)) {
      return toast('protec_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }
    if (role === 'ADMIN' && clinic_id && !ID_CHAR_REGEX.test(clinic_id)) {
      return toast('clinic_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }

    if (!strongPwOk(protec_password, protec_id)) {
      return toast('비밀번호는 10~32자, 공백X, 3종 이상, ID 포함 금지, 허용 특수문자만 사용');
    }
    setLoading(true);
    try {
      const body = { protec_id, protec_password, status };
      if (role === 'ADMIN') body.clinic_id = clinic_id;
      const { data } = await api.post('/manager/user/clinic/protectors', body);
      if (isOk(data)) {
        toast('생성 완료');
        setCreateForm({ clinic_id: '', protec_id: '', protec_password: '', status: 'ACTIVE' });
        fetchList(1);
      } else {
        toast(getMsg(data, '생성 실패'));
      }
    } catch (e) {
      if (e?.response?.status === 409) toast('중복된 계정입니다.');
      else if (e?.response?.status === 401) toast('세션 만료. 다시 로그인 해주세요.');
      else if (e?.response?.status === 403) toast('권한이 없습니다.');
      else if (e?.response?.status === 406) toast('비밀번호 정책 위반');
      else toast('생성 실패');
    } finally { setLoading(false); }
  };

  const confirmAnd = async (m, fn) => { if (window.confirm(m)) await fn(); };

  // 미배정 사용자 후보 가져오기
  const fetchUnassignedUsers = async (limit = 20) => {
    try {
      const { data } = await api.get('/manager/user/clinic/protectors/unassigned', { params: { limit } });
      const rows = Array.isArray(data?.data) ? data.data : [];
      return rows.map(r => r.user_id);
    } catch {
      return [];
    }
  };

  // 빠른 배정
  const quickBind = async (protec_id) => {
    setAssigning(protec_id);
    try {
      const candidates = await fetchUnassignedUsers(30);
      if (!candidates.length) {
        const manual = window.prompt('미배정 사용자가 없습니다. 직접 user_id를 입력하세요:');
        if (!manual) return;

        // ID 형식 검증
        if (!ID_CHAR_REGEX.test(manual.trim())) {
          toast('user_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
          return;
        }

        const { data } = await api.post('/manager/user/clinic/protectors/bind', {
          user_id: manual.trim(),
          protec_id,
        });
        if (isOk(data)) { toast('배정 완료'); fetchList(page); } else { toast(getMsg(data, '배정 실패')); }
        return;
      }
      const hint = candidates.slice(0, 10).join(', ');
      const picked = window.prompt(`배정할 user_id를 입력하세요.\n추천: ${hint}${candidates.length > 10 ? ' ...' : ''}`);
      if (!picked) return;

      if (!ID_CHAR_REGEX.test(picked.trim())) {
        toast('user_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
        return;
      }

      const { data } = await api.post('/manager/user/clinic/protectors/bind', {
        user_id: picked.trim(),
        protec_id,
      });
      if (isOk(data)) { toast('배정 완료'); fetchList(page); } else { toast(getMsg(data, '배정 실패')); }
    } catch {
      toast('배정 실패');
    } finally {
      setAssigning(null);
    }
  };

  // 빠른 해제
  const quickUnbind = async (protec_id) => {
    const userId = window.prompt('해제할 user_id를 입력하세요:');
    if (!userId) return;

    if (!ID_CHAR_REGEX.test(userId.trim())) {
      toast('user_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
      return;
    }

    setAssigning(protec_id);
    try {
      const { data } = await api.post('/manager/user/clinic/protectors/unbind', {
        user_id: userId.trim(),
        protec_id,
      });
      if (isOk(data)) { toast('해제 완료'); fetchList(page); } else { toast(getMsg(data, '해제 실패')); }
    } catch {
      toast('해제 실패');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Menu>
      <div className="account-page">
        <h1 className="text-2xl font-bold">보호자 계정 관리</h1>

        {/* 목록 */}
        <section className="border rounded p-4 space-y-3">
          <h2 className="font-semibold">계정 목록</h2>

          {/* 검색/컨트롤 */}
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="flex gap-2 flex-1">
              <input
                className="border rounded px-3 py-2 w-full"
                placeholder="protec_id 검색 (2자 이상)"
                value={q}
                onChange={(e) => setQ(filterSearchValue(e.target.value))}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchList(1); }}
                onBeforeInput={handleBeforeInputSearch}
              />
              <select
                className="border rounded px-3 py-2"
                value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
                aria-label="페이지당 개수"
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}개</option>)}
              </select>
              <button
                type="button"
                onClick={() => fetchList(1)}
                disabled={loading}
                className="px-4 py-2 rounded border border-slate-400 text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50 transition-colors"
              >
                검색
              </button>
            </div>
          </div>

          {/* 테이블 */}
          <div className="overflow-x-auto border rounded mt-2">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">protec_id</th>
                  <th className="text-left p-2">status</th>
                  <th className="text-left p-2">pw_changed_at</th>
                  <th className="text-left p-2">last_login</th>
                  <th className="text-left p-2">actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.user_id} className="border-t">
                    <td className="p-2 font-medium">{row.user_id}</td>
                    <td className="p-2"><Badge s={row.status} /></td>
                    <td className="p-2">
                      {row.pw_changed_at ? new Date(row.pw_changed_at).toLocaleString() : '-'}
                    </td>
                    <td className="p-2">
                      {row.last_login ? new Date(row.last_login).toLocaleString() : '-'}
                    </td>
                    <td className="p-2">
                      {/* 상태 토글 */}
                      {row.status === 'ACTIVE' ? (
                        <button
                          className="px-2 py-1 rounded border text-black mr-2"
                          onClick={() =>
                            confirmAnd('해당 계정을 잠금(LOCK) 처리할까요?', () => changeStatus(row.user_id, 'LOCKED'))
                          }
                          disabled={loading}
                        >잠금</button>
                      ) : (
                        <button
                          className="px-2 py-1 rounded bg-green-600 text-black mr-2"
                          onClick={() =>
                            confirmAnd('해당 계정을 활성화(ACTIVE)로 변경할까요?', () => changeStatus(row.user_id, 'ACTIVE'))
                          }
                          disabled={loading}
                        >해제</button>
                      )}

                      {/* 비번 변경 */}
                      <input
                        type="password"
                        className="border rounded px-2 py-1 mr-2"
                        placeholder="새 비밀번호"
                        value={pwMap[row.user_id] || ''}
                        onChange={(e) =>
                          setPwMap(m => ({ ...m, [row.user_id]: filterPwValue(e.target.value) }))
                        }
                        onBeforeInput={handleBeforeInputPw}
                        style={{ width: 180 }}
                      />
                      <button
                        className="px-2 py-1 rounded border mr-3"
                        onClick={() =>
                          confirmAnd('비밀번호를 변경하시겠습니까?', () => changePassword(row.user_id))
                        }
                        disabled={loading || !(pwMap[row.user_id] || '').length}
                      >변경</button>

                      {/* 빠른 매칭/해제 */}
                      {/* <button
                        type="button"
                        className="px-2 py-1 rounded border mr-2"
                        onClick={() => quickBind(row.user_id)}
                        disabled={loading || assigning === row.user_id}
                        title="미배정 사용자에서 선택하여 배정"
                      >
                        {assigning === row.user_id ? '배정 중…' : '사용자 배정'}
                      </button>
                      <button
                        type="button"
                        className="px-2 py-1 rounded border"
                        onClick={() => quickUnbind(row.user_id)}
                        disabled={loading || assigning === row.user_id}
                        title="입력한 user_id와의 연결 해제"
                      >
                        배정 해제
                      </button> */}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td className="p-4 text-center text-gray-500" colSpan={5}>
                      데이터가 없습니다
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              총 {total}건 / {page} / {totalPages} 페이지
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page <= 1 || loading}
                onClick={() => fetchList(page - 1)}
              >
                이전
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page >= totalPages || loading}
                onClick={() => fetchList(page + 1)}
              >
                다음
              </button>
            </div>
          </div>
        </section>

        {/* 생성 */}
        <section className="border rounded p-4 space-y-3">
          <h2 className="font-semibold">보호자 계정 생성</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* ADMIN만 clinic_id 입력 */}
            {role === 'ADMIN' && (
              <input
                className="border rounded px-3 py-2"
                placeholder="clinic_id (ADMIN 전용)"
                value={createForm.clinic_id}
                onChange={(e) =>
                  setCreateForm(f => ({ ...f, clinic_id: filterIdValue(e.target.value) }))
                }
                onBeforeInput={handleBeforeInputId}
              />
            )}
            <input
              className="border rounded px-3 py-2"
              placeholder="protec_id"
              value={createForm.protec_id}
              onChange={(e) =>
                setCreateForm(f => ({ ...f, protec_id: filterIdValue(e.target.value) }))
              }
              onKeyDown={(e) => { if (e.key === 'Enter') createProtec(); }}
              onBeforeInput={handleBeforeInputId}
            />
            <input
              type="password"
              className="border rounded px-3 py-2"
              placeholder="password (10~32자, 공백없음)"
              value={createForm.protec_password}
              onChange={(e) =>
                setCreateForm(f => ({ ...f, protec_password: filterPwValue(e.target.value) }))
              }
              onKeyDown={(e) => { if (e.key === 'Enter') createProtec(); }}
              onBeforeInput={handleBeforeInputPw}
            />
            <select
              className="border rounded px-3 py-2"
              value={createForm.status}
              onChange={(e) => setCreateForm(f => ({ ...f, status: e.target.value }))}
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="LOCKED">LOCKED</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={createProtec}
              disabled={loading}
              className="px-4 py-2 rounded border text-black disabled:opacity-50"
            >
              생성
            </button>
            <button
              onClick={() => setCreateForm({ clinic_id: '', protec_id: '', protec_password: '', status: 'ACTIVE' })}
              className="px-4 py-2 rounded border"
            >
              초기화
            </button>
          </div>
          <p className="text-xs text-gray-500">
            * 비밀번호는 10~32자, 공백 금지, 대/소문자/숫자/허용 특수문자 중 3종류 이상 포함, ID 포함 금지
          </p>
        </section>

        {/* 보호자-사용자 연결 (간단 입력형) */}
        <BindBox
          toast={toast}
          refresh={() => fetchList(page)}
          handleBeforeInputId={handleBeforeInputId}
        />
      </div>

      {/* 토스트 */}
      {msg && (
        <div className="fixed bottom-4 right-4 z-50 text-sm px-3 py-2 rounded bg-black text-white shadow">
          {msg}
        </div>
      )}
    </Menu>
  );
}

// ───────────── 하위: 보호자-사용자 연결 ─────────────
function BindBox({ toast, refresh, handleBeforeInputId }) {
  const [userId, setUserId] = useState('');
  const [protecId, setProtecId] = useState('');
  const [loading, setLoading] = useState(false);

  const bind = async () => {
    if (!userId || !protecId) return toast('user_id, protec_id 입력');

    if (!ID_CHAR_REGEX.test(userId) || !ID_CHAR_REGEX.test(protecId)) {
      return toast('user_id / protec_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }

    setLoading(true);
    try {
      const { data } = await api.post('/manager/user/clinic/protectors/bind', {
        user_id: userId,
        protec_id: protecId,
      });
      if (isOk(data)) {
        toast('연결 완료');
        setUserId('');
        setProtecId('');
        refresh();
      } else {
        toast(getMsg(data, '연결 실패'));
      }
    } catch {
      toast('연결 실패');
    } finally {
      setLoading(false);
    }
  };

  const unbind = async () => {
    if (!userId) return toast('user_id 입력');
    if (!protecId) return toast('protec_id 입력');

    if (!ID_CHAR_REGEX.test(userId) || !ID_CHAR_REGEX.test(protecId)) {
      return toast('user_id / protec_id 는 영문/숫자 및 . - _ 만 사용 가능합니다.');
    }

    setLoading(true);
    try {
      const { data } = await api.post('/manager/user/clinic/protectors/unbind', {
        user_id: userId,
        protec_id: protecId,
      });
      if (isOk(data)) {
        toast('해제 완료');
        setUserId('');
        setProtecId('');
        refresh();
      } else {
        toast(getMsg(data, '해제 실패'));
      }
    } catch {
      toast('해제 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="border rounded p-4 space-y-3">
      <h2 className="font-semibold">보호자 ↔ 사용자 연결</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input
          className="border rounded px-3 py-2"
          placeholder="user_id"
          value={userId}
          onChange={e => setUserId(filterIdValue(e.target.value))}
          onBeforeInput={handleBeforeInputId}
        />
        <input
          className="border rounded px-3 py-2"
          placeholder="protec_id"
          value={protecId}
          onChange={e => setProtecId(filterIdValue(e.target.value))}
          onBeforeInput={handleBeforeInputId}
        />
        <div className="flex gap-2">
          <button type="button" className="px-4 py-2 rounded border" onClick={bind} disabled={loading}>
            연결
          </button>
          <button type="button" className="px-4 py-2 rounded border" onClick={unbind} disabled={loading}>
            해제
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        * 연결은 해당 클리닉이 관리 중인 user_id 기준으로만 동작합니다.
      </p>
    </section>
  );
}
